// site-dashboard.component.ts
import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {DashboardApi} from '@/sdk/api-services';
import {BarSeriesOption, LegendComponentOption, PieSeriesOption, TitleComponentOption, TooltipComponentOption} from 'echarts';
import {Component, Prop, Watch} from 'vue-property-decorator';
import AreasAndEquipmentsCountChart from '../shared/charts/areas-and-equipments-count-chart.component';
import DonutChartComponent from '../shared/charts/donut-chart.component';
import DustAreaEquipmentsCountChart from '../shared/charts/dust-area-equipments-count-chart.component';
import InspectionQuestionsClassificationChart from '../shared/charts/inspection-questions-classification-chart.component';
import MultilayerDonutChartComponent from '../shared/charts/multilayer-donut-chart.component';
import TimeSeriesLineChartComponent, {RawChartDataItem} from '../shared/charts/timeseries-line-chart.component';
import AssetManagementWarningsListComponent from '../shared/warnings-list/asset-management-warnings-list.component.vue';
import {
    default as inspectionsAndCAWarningsListComponent,
    default as InspectionsAndCAWarningsListComponent,
} from '../shared/warnings-list/inspections-and-ca-warnings-list.component';
import SiteWarningsListComponent from '../shared/warnings-list/site-warnings-list.component';

interface DashboardCard {
    key: string;
    overline: string;
    value: string | number;
    subtitle: string;
    icon: string;
    color: string;
    iconColor: string;
}

interface MinimalStatCard {
    key: string;
    value: string | number;
    label: string;
    icon: string;
    color: string;
    iconColor: string;
}

type WebsiteRefererOption = echarts.ComposeOption<PieSeriesOption | TooltipComponentOption | LegendComponentOption | TitleComponentOption>;

interface TimeSeriesApiResponseItem {
    Time: string;
    Month: string;
    Areas: number;
    Equipments: number;
}

interface ZoneCount {
    zoneName: string;
    equipmentCount: number;
}

interface ZoneCountResponse {
    gasZoneCounts: ZoneCount[];
    dustZoneCounts: ZoneCount[];
}

interface ChartSeriesData extends BarSeriesOption {
    stack?: string; // Added stack property for stacked bar charts
}

interface ChartData {
    yAxisData: string[]; // Changed from xAxisData to yAxisData for horizontal bar chart
    seriesData: ChartSeriesData[];
    title?: string;
    xAxisLabel?: string; // Changed from yAxisLabel to xAxisLabel
}

@Component({
    components: {
        SiteWarningsListComponent,
        DonutChartComponent,
        TimeSeriesLineChartComponent,
        MultilayerDonutChartComponent,
        AreasAndEquipmentsCountChart,
        DustAreaEquipmentsCountChart,
        InspectionQuestionsClassificationChart,
        AssetManagementWarningsListComponent,
        InspectionsAndCAWarningsListComponent,
    },
})
export default class SiteDashboardComponent extends VueWrapper {
    public $refs!: {
        siteWarningsList: SiteWarningsListComponent;
        donutChart: DonutChartComponent;
        timeseriesLineChart: TimeSeriesLineChartComponent;
        multilayerDonutChart: MultilayerDonutChartComponent;
        areasAndEquipmentsCountChart: AreasAndEquipmentsCountChart;
        dustAreaEquipmentsCountChart: DustAreaEquipmentsCountChart;
        inspectionQuestionsClassificationChart: InspectionQuestionsClassificationChart;
        assetManagementWarningsList: AssetManagementWarningsListComponent;
        inspectionsAndCAWarningsList: inspectionsAndCAWarningsListComponent;
    };

    public dashboardApi: DashboardApi = new DashboardApi();

    isLoading = false;
    hasError = false;
    errorMessage = '';

    @Prop({type: String, default: ''})
    siteIdProp!: string;

    rightDrawerForAssetManagementWarnings = false;
    rightDrawerWidthForAssetManagementWarnings = 600;
    rightDrawerForInspectionsAndCAWarnings = false;
    rightDrawerWidthForInspectionsAndCAWarnings = 600;
    rightDrawerForIotWarnings = false;
    rightDrawerWidthForIotWarnings = 600;

    public timeSeriesRawData: RawChartDataItem[] = [];
    public timeSeriesTimeKey: string = 'Time';
    public timeSeriesValueKeys: string[] = ['Areas', 'Equipments'];

    public assetManagementWarningsCount: number = 0;
    public inspectionsAndCAWarningsCount: number = 0;
    public iotWarningsCount: number = 0;

    public areasAndEquipmentsDashboardCards: MinimalStatCard[] = [
        {
            key: 'areasCount',
            value: '0',
            label: 'Areas',
            icon: 'mdi-map',
            color: 'orange lighten-4',
            iconColor: 'orange darken-2',
        },
        {
            key: 'subAreasCount',
            value: '0',
            label: 'Sub-areas',
            icon: 'mdi-map-marker',
            color: 'orange lighten-4',
            iconColor: 'orange darken-2',
        },
        {
            key: 'equipmentCount',
            value: '0',
            label: 'Equipments',
            icon: 'mdi-tools',
            color: 'purple lighten-4',
            iconColor: 'purple darken-2',
        },
    ];

    correctiveActionsDashboardCards: DashboardCard[] = [
        {
            key: 'completedCorrectiveActions',
            overline: 'Completed',
            value: '0',
            subtitle: 'Corrective actions',
            icon: 'mdi-check-circle',
            color: 'green lighten-4',
            iconColor: 'green darken-2',
        },
        {
            key: 'onGoingCorrectiveActions',
            overline: 'On-going',
            value: '0',
            subtitle: 'Corrective actions',
            icon: 'mdi-progress-check',
            color: 'blue lighten-4',
            iconColor: 'blue darken-2',
        },
    ];

    inspectionsDashboardCards: MinimalStatCard[] = [
        {
            key: 'completedInspections',
            value: '0',
            label: 'Completed',
            icon: 'mdi-check-circle',
            color: 'green darken-1',
            iconColor: 'white',
        },
        {
            key: 'onGoingInspections',
            value: '0',
            label: 'On-going',
            icon: 'mdi-progress-clock',
            color: 'blue darken-1',
            iconColor: 'white',
        },
        {
            key: 'delayedInspections',
            value: '0',
            label: 'Delayed',
            icon: 'mdi-clock-alert',
            color: 'amber darken-2',
            iconColor: 'white',
        },
        {
            key: 'asPlannedInspections',
            value: '0',
            label: 'As Planned',
            icon: 'mdi-calendar-check',
            color: 'teal darken-1',
            iconColor: 'white',
        },
    ];

    public zoneData: ZoneCountResponse | null = null;
    public isLoadingZoneData = false;

    public areasAndEquipmentsCountChartData: WebsiteRefererOption = {
        xAxisData: ['Jan 2024', 'Feb 2024', 'Mar 2024', 'Apr 2024'],
        seriesData: [
            {
                name: 'Areas',
                type: 'bar',
                barGap: 0,
                emphasis: {focus: 'series'},
                data: [182, 234, 290, 250],
                label: {show: true, position: 'inside'},
            },
            {
                name: 'Equipments',
                type: 'bar',
                emphasis: {focus: 'series'},
                data: [121, 134, 180, 195],
                label: {show: true, position: 'inside'},
            },
        ],
    };

    public dustAreaEquipmentsCountChartData: WebsiteRefererOption = {
        seriesData: {
            type: 'pie',
            radius: '60%',
            data: [
                {value: 35, name: 'Operational'},
                {value: 10, name: 'Maintenance'},
                {value: 5, name: 'Offline'},
                {value: 8, name: 'Standby'},
            ],
        },
    };

    public gasAreaEquipmentsCountChartData: WebsiteRefererOption = {
        seriesData: {
            type: 'pie',
            radius: '60%',
            data: [
                {value: 35, name: 'Operational'},
                {value: 10, name: 'Maintenance'},
                {value: 5, name: 'Offline'},
                {value: 8, name: 'Standby'},
            ],
        },
    };

    public inspectionChartDataForFailPassNA: ChartData = {
        yAxisData: ['Inspection Result'],
        seriesData: [
            {
                name: 'Direct',
                type: 'bar',
                stack: 'total', // Key for stacking
                label: {show: true},
                emphasis: {focus: 'series'},
                data: [320, 302, 301, 334, 390, 330, 320],
            },
        ],
    };

    public inspectionChartDataForGrade: ChartData = {
        yAxisData: ['Inspection Result'],
        seriesData: [
            {
                name: 'Direct',
                type: 'bar',
                stack: 'total', // Key for stacking
                label: {show: true},
                emphasis: {focus: 'series'},
                data: [320, 302, 301, 334, 390, 330, 320],
            },
        ],
    };

    get siteId(): string {
        return this.siteIdProp || (this.$route?.params?.siteId as string) || '';
    }

    created(): void {
        if (this.siteId) {
            this.getSiteStatistics();
            this.fetchEquipmentsAndAreasCount();
            this.fetchZoneData();
            this.getInspectionQuestionsStatistics();
        }
    }

    @Watch('siteId')
    onSiteIdChanged(newVal: string): void {
        if (newVal) {
            this.getSiteStatistics();
            this.fetchEquipmentsAndAreasCount();
            this.fetchZoneData();
            this.getInspectionQuestionsStatistics();
        } else {
            this.timeSeriesRawData = [];
            this.areasAndEquipmentsCountChartData = {
                xAxisData: [],
                seriesData: [],
            };
            this.dustAreaEquipmentsCountChartData = {
                seriesData: {
                    type: 'pie',
                    radius: '60%',
                    data: [],
                },
            };
            this.gasAreaEquipmentsCountChartData = {
                seriesData: {
                    type: 'pie',
                    radius: '60%',
                    data: [],
                },
            };
            this.zoneData = null;
            this.isLoadingZoneData = false;
            this.hasError = false;
            this.errorMessage = '';
        }
    }

    async fetchZoneData(): Promise<void> {
        if (!this.siteId) {
            this.hasError = true;
            this.errorMessage = 'No site ID provided';
            return;
        }

        try {
            this.isLoading = true;
            this.hasError = false;

            if (this.LoaderSrv) {
                this.LoaderSrv.showFullScreenLoader();
            }

            const response: ZoneCountResponse = await this.dashboardApi.getZoneEquipmentCounts(this.siteId);
            this.zoneData = response;

            this.dustAreaEquipmentsCountChartData = {
                seriesData: {
                    type: 'pie',
                    radius: '60%',
                    data: response.dustZoneCounts.map(zone => ({
                        value: zone.equipmentCount,
                        name: zone.zoneName,
                    })),
                },
            };
            this.gasAreaEquipmentsCountChartData = {
                seriesData: {
                    type: 'pie',
                    radius: '60%',
                    data: response.gasZoneCounts.map(zone => ({
                        value: zone.equipmentCount,
                        name: zone.zoneName,
                    })),
                },
            };
            this.updateDustAreaEquipmentsCountChartData();
        } catch (error) {
            this.hasError = true;
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load zone data';
            console.error('Error fetching zone data:', error);
        } finally {
            this.isLoading = false;
            if (this.LoaderSrv) {
                this.LoaderSrv.hideFullScreenLoader();
            }
        }
    }

    async fetchEquipmentsAndAreasCount(): Promise<void> {
        if (!this.siteId) {
            this.hasError = true;
            this.errorMessage = 'No site ID provided';
            return;
        }

        this.isLoading = true;
        this.hasError = false;

        if (this.LoaderSrv) this.LoaderSrv.showFullScreenLoader();

        try {
            const response: TimeSeriesApiResponseItem[] = await this.getEquipmentsAndSitesCountByTime();
            this.timeSeriesRawData = response.map(item => ({
                Time: item.Time,
                Month: item.Month,
                Areas: item.Areas,
                Equipments: item.Equipments,
            }));

            const filteredData = this.timeSeriesRawData.filter(
                item => (typeof item.Areas === 'number' && item.Areas > 0) || (typeof item.Equipments === 'number' && item.Equipments > 0)
            );
            this.timeSeriesRawData = filteredData.length > 0 ? filteredData : this.timeSeriesRawData;

            this.areasAndEquipmentsCountChartData = {
                xAxisData: this.timeSeriesRawData.map(item => item.Month),
                seriesData: [
                    {
                        name: 'Areas',
                        type: 'bar',
                        barGap: 0,
                        emphasis: {focus: 'series'},
                        data: this.timeSeriesRawData.map(item => item.Areas),
                        label: {show: true, position: 'inside'},
                    },
                    {
                        name: 'Equipments',
                        type: 'bar',
                        emphasis: {focus: 'series'},
                        data: this.timeSeriesRawData.map(item => item.Equipments),
                        label: {show: true, position: 'inside'},
                    },
                ],
            };
        } catch (error) {
            this.hasError = true; // Set general error flag
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load time series data';
            console.error('Error fetching time series data:', error);
            this.timeSeriesRawData = []; // Clear data on error
        } finally {
            this.isLoading = false;
            if (this.LoaderSrv) this.LoaderSrv.hideFullScreenLoader();
        }
    }

    private async getEquipmentsAndSitesCountByTime(): Promise<TimeSeriesApiResponseItem[]> {
        const response = await this.dashboardApi.getLocationCountOfEachTypeByTimeType(this.siteId, 'areas');
        return response.map(item => ({
            Time: item.Time,
            Month: item.Month,
            Areas: item.Areas,
            Equipments: item.Equipments,
        }));
    }

    async getSiteStatistics(): Promise<void> {
        if (!this.siteId) {
            this.hasError = true;
            this.errorMessage = 'No site ID provided';
            return;
        }

        try {
            this.isLoading = true;
            this.hasError = false;

            if (this.LoaderSrv) {
                this.LoaderSrv.showFullScreenLoader();
            }

            // Fetch site statistics from API
            const response = await this.dashboardApi.getSiteStatistics(this.siteId);

            // Update dashboard cards with API data
            this.updateDashboardData(response);
        } catch (error) {
            this.hasError = true;
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load site statistics';
            console.error('Error fetching site statistics:', error);
        } finally {
            this.isLoading = false;
            if (this.LoaderSrv) {
                this.LoaderSrv.hideFullScreenLoader();
            }
        }
    }

    async getInspectionQuestionsStatistics(): Promise<void> {
        if (!this.siteId) {
            this.hasError = true;
            this.errorMessage = 'No site ID provided';
            return;
        }

        try {
            this.isLoading = true;
            this.hasError = false;

            if (this.LoaderSrv) {
                this.LoaderSrv.showFullScreenLoader();
            }

            // Fetch inspection questions statistics from API
            const response = await this.dashboardApi.getInspectionQuestionsStatistics(this.siteId);

            const {inspectionQuestionsCountAccordingToFailPassNA, inspectionQuestionsCountAccordingToGrade} = response;
            const failPassNASeriesData = inspectionQuestionsCountAccordingToFailPassNA.TotalQuestions.map((count, index) => ({
                name: inspectionQuestionsCountAccordingToFailPassNA.AnswerStatus[index],
                type: 'bar',
                stack: 'total', // Key for stacking
                label: {show: true},
                emphasis: {focus: 'series'},
                data: [count],
            }));

            const gradeSeriesData = inspectionQuestionsCountAccordingToGrade.TotalQuestions.map((count, index) => ({
                name: inspectionQuestionsCountAccordingToGrade.Grade[index],
                type: 'bar',
                stack: 'total', // Key for stacking
                label: {show: true},
                emphasis: {focus: 'series'},
                data: [count],
            }));

            this.inspectionChartDataForFailPassNA.seriesData = failPassNASeriesData as ChartSeriesData[];
            this.inspectionChartDataForFailPassNA.yAxisData = ['Inspection Result'];

            this.inspectionChartDataForGrade.seriesData = gradeSeriesData as ChartSeriesData[];
            this.inspectionChartDataForGrade.yAxisData = ['Inspection Grade'];
        } catch (error) {
            this.hasError = true;
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load inspection questions statistics';
            console.error('Error fetching inspection questions statistics:', error);
        } finally {
            this.isLoading = false;
            if (this.LoaderSrv) {
                this.LoaderSrv.hideFullScreenLoader();
            }
        }
    }

    private updateDashboardData(data: Record<string, any>): void {
        this.iotWarningsCount = data.warningsCount || 0;
        this.assetManagementWarningsCount = data.assetManagementWarningsCount || 0;
        this.inspectionsAndCAWarningsCount = data.inspectionsAndCAWarningsCount || 0;
        this.areasAndEquipmentsDashboardCards.forEach(card => {
            if (data[card.key] !== undefined) {
                card.value = data[card.key];
            }
        });
        this.correctiveActionsDashboardCards.forEach(card => {
            if (data[card.key] !== undefined) {
                card.value = data[card.key];
            }
        });
        this.inspectionsDashboardCards.forEach(card => {
            if (data[card.key] !== undefined) {
                card.value = data[card.key];
            }
        });
    }

    private updateDustAreaEquipmentsCountChartData(): void {
        const data = this.zoneData;
    }
}
